export const formatCaseMoney = (value, digits = 0) => value == null || Number.isNaN(Number(value))
  ? '—'
  : Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits });

export const formatCaseNumber = (value, digits = 0) => value == null || Number.isNaN(Number(value))
  ? '—' : Number(value).toLocaleString('en-US', { maximumFractionDigits: digits });

export const caseSquareFootage = (lead) => lead?.modeled_square_footage ?? lead?.square_footage ?? null;

export const casePricePerSqft = (lead) => {
  if (lead?.asking_price == null || lead?.asking_price === '' || caseSquareFootage(lead) == null || caseSquareFootage(lead) === '') return null;
  const askingPrice = Number(lead?.asking_price);
  const squareFootage = Number(caseSquareFootage(lead));
  return Number.isFinite(askingPrice) && Number.isFinite(squareFootage) && squareFootage > 0 ? askingPrice / squareFootage : null;
};

export const POTENTIAL_BEDROOM_SQFT_TARGET = 550;
export const POTENTIAL_BEDROOM_SQFT_TARGET_LOW = 525;

export const potentialExtraBedrooms = (squareFootage, currentBedrooms, targetSqFtPerBedroom = POTENTIAL_BEDROOM_SQFT_TARGET) => {
  if (squareFootage == null || squareFootage === '' || currentBedrooms == null || currentBedrooms === '') return null;
  const totalSqFt = Number(squareFootage); const beds = Number(currentBedrooms); const target = Number(targetSqFtPerBedroom);
  if (!Number.isFinite(totalSqFt) || totalSqFt <= 0 || !Number.isFinite(beds) || beds < 0 || !Number.isFinite(target) || target <= 0) return null;
  return Math.max(0, Math.floor(totalSqFt / target) - Math.floor(beds));
};

export const potentialExtraBedroomRange = (squareFootage, currentBedrooms) => {
  const low = potentialExtraBedrooms(squareFootage, currentBedrooms, POTENTIAL_BEDROOM_SQFT_TARGET);
  const high = potentialExtraBedrooms(squareFootage, currentBedrooms, POTENTIAL_BEDROOM_SQFT_TARGET_LOW);
  return low == null || high == null ? null : { low, high, label: low === high ? String(low) : `${low}–${high}` };
};

export const scenarioRange = (scenario, compact = false) => {
  if (!scenario) return '—';
  const formatter = compact
    ? (value) => Math.abs(Number(value)) >= 1000 ? `$${Math.round(Number(value) / 1000)}K` : formatCaseMoney(value)
    : formatCaseMoney;
  return Number(scenario.low) === Number(scenario.high) ? formatter(scenario.low) : `${formatter(scenario.low)}–${formatter(scenario.high)}`;
};

export const caseMaxRehab = (lead) => {
  if (lead?.arv_scenario?.low == null || lead?.asking_price == null) return null;
  const arv = Number(lead?.arv_scenario?.low);
  const asking = Number(lead?.asking_price);
  return Number.isFinite(arv) && arv > 0 && Number.isFinite(asking) ? (arv * 0.75) - asking : null;
};

export const pairedRentArvRatioRange = (rentScenario, arvScenario) => {
  const rentLow = Number(rentScenario?.low);
  const rentHigh = Number(rentScenario?.high);
  const arvLow = Number(arvScenario?.low);
  const arvHigh = Number(arvScenario?.high);
  if (![rentLow, rentHigh, arvLow, arvHigh].every(Number.isFinite) || arvLow <= 0 || arvHigh <= 0) return null;
  const lowRentLowArv = rentLow / arvLow;
  const highRentHighArv = rentHigh / arvHigh;
  return {
    lowRentLowArv,
    highRentHighArv,
    low: Math.min(lowRentLowArv, highRentHighArv),
    high: Math.max(lowRentLowArv, highRentHighArv),
  };
};

export const caseRentArvRange = (lead) => {
  const range = pairedRentArvRatioRange(lead?.rent_scenario, lead?.arv_scenario);
  return range ? `${(range.lowRentLowArv * 100).toFixed(2)}%–${(range.highRentHighArv * 100).toFixed(2)}%` : null;
};

export const ONE_PERCENT_RULE_PASS_THRESHOLD = 0.008;

export const pairedRentArvResults = (rentScenario, arvScenario, threshold = ONE_PERCENT_RULE_PASS_THRESHOLD) => {
  const ratios = pairedRentArvRatioRange(rentScenario, arvScenario);
  if (!ratios) return null;
  return [
    { key: 'low', label: 'Low rent ÷ Low ARV', value: ratios.lowRentLowArv, formatted: `${(ratios.lowRentLowArv * 100).toFixed(2)}%`, tone: ratios.lowRentLowArv >= threshold ? 'positive' : 'negative' },
    { key: 'high', label: 'High rent ÷ High ARV', value: ratios.highRentHighArv, formatted: `${(ratios.highRentHighArv * 100).toFixed(2)}%`, tone: ratios.highRentHighArv >= threshold ? 'positive' : 'negative' },
  ];
};

export const caseOnePercentRuleTone = (lead, threshold = ONE_PERCENT_RULE_PASS_THRESHOLD) => {
  const range = pairedRentArvRatioRange(lead?.rent_scenario, lead?.arv_scenario);
  if (!range) return 'unavailable';
  const lowTone = range.lowRentLowArv >= threshold ? 'positive' : 'negative';
  const highTone = range.highRentHighArv >= threshold ? 'positive' : 'negative';
  return lowTone === highTone ? lowTone : 'mixed';
};

export const caseOnePercentRuleResults = (lead, threshold = ONE_PERCENT_RULE_PASS_THRESHOLD) => {
  return pairedRentArvResults(lead?.rent_scenario, lead?.arv_scenario, threshold);
};

export const caseRehabPerSqft = (lead) => {
  const allowance = caseMaxRehab(lead);
  const sqft = Number(caseSquareFootage(lead));
  return Number.isFinite(allowance) && Number.isFinite(sqft) && sqft > 0 ? allowance / sqft : null;
};

export const caseStatusLabel = (status) => ({ screening: 'Screening', needs_data: 'Needs Data', under_review: 'Under Review' }[status] || 'Screening');
